/**
 * What kind of device a session is on — from the browser's User-Agent.
 *
 * Browsers never expose a device's own name ("Ravi's iPhone" in Settings), so
 * we only take what identifies a device for the owner: its kind (iPhone, iPad,
 * Android phone, Mac…) and the browser (or "installed app"). A real name comes
 * from the owner renaming the device on /sessions (rename-device in
 * api/_sessions.js); until then the title is "Ravi's iPhone".
 */

/**
 * Parse a User-Agent → { platform, browser, app, label }.
 * `touchPoints` tells an iPad in desktop mode (it reports as a Mac) apart;
 * `standalone` means it runs as the installed app (PWA).
 */
export function parseUserAgent(ua = '', { touchPoints = 0, standalone = false } = {}) {
  let platform = 'Unknown device';
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && touchPoints > 1)) platform = 'iPad';
  else if (/iPhone|iPod/.test(ua)) platform = 'iPhone';
  else if (/Android/.test(ua)) platform = /Mobile/.test(ua) ? 'Android phone' : 'Android tablet';
  else if (/CrOS/.test(ua)) platform = 'Chromebook';
  else if (/Windows/.test(ua)) platform = 'Windows PC';
  else if (/Macintosh|Mac OS X/.test(ua)) platform = 'Mac';
  else if (/Linux/.test(ua)) platform = 'Linux PC';

  let browser = 'Browser';
  if (/Edg(e|A|iOS)?\//.test(ua)) browser = 'Edge';
  else if (/OPR\/|Opera/.test(ua)) browser = 'Opera';
  else if (/SamsungBrowser/.test(ua)) browser = 'Samsung Internet';
  else if (/FxiOS|Firefox\//.test(ua)) browser = 'Firefox';
  else if (/CriOS|Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Safari\//.test(ua)) browser = 'Safari';

  const app = !!standalone;
  return { platform, browser, app, label: `${platform} · ${app ? 'installed app' : browser}` };
}

/** This browser's device info (for registering its session). */
export function currentDeviceInfo() {
  if (typeof navigator === 'undefined') return parseUserAgent('');
  const standalone = (typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches)
    || navigator.standalone === true;
  return parseUserAgent(navigator.userAgent || '', { touchPoints: navigator.maxTouchPoints || 0, standalone });
}

// ── Display ─────────────────────────────────────────────────────────────────

const firstName = (name) => String(name || '').trim().split(/\s+/)[0] || '';

/**
 * Title for a session: the name the owner gave the device, else the
 * automatic "Ravi’s iPhone" / "Your Mac".
 */
export function deviceTitle(session, personName, { self = false } = {}) {
  if (session?.nickname) return session.nickname;
  const kind = session?.device?.platform || 'device';
  if (self) return `Your ${kind}`;
  const who = firstName(personName);
  return who ? `${who}’s ${kind}` : kind;
}

/** "iPhone · Safari" / "iPad · Installed app". */
export function deviceSubtitle(d) {
  if (!d) return '';
  return [d.platform, d.app ? 'Installed app' : d.browser].filter(Boolean).join(' · ');
}
